import type { Annotation, KeypointAnnotation, UserAdjustment } from "@/types/annotations";
import {
  getMeasurementAxisValue,
  measurementFieldPathForAxisKey,
  parseMeasurementEndpointName,
  withMeasurementAxisValue,
  withoutMeasurementAxisValue,
} from "@shared/types/measurementDescriptors";
import { buildMeasurementLineDrawSpecs } from "@/utils/annotationMeasurementUtils";
import { isMeasurementAxisValued } from "./helpers";

export type MeasurementKeypointName = string;

const isMeasurementKeypointName = (name: string | undefined): name is MeasurementKeypointName =>
  parseMeasurementEndpointName(name) !== null;

interface MeasurementAxisPair {
  key: string;
  start: KeypointAnnotation;
  end: KeypointAnnotation;
}

function findMeasurementAxisForEndpoint(annotation: Annotation, keypointName: string): MeasurementAxisPair | null {
  const parsedEndpoint = parseMeasurementEndpointName(keypointName);
  if (!parsedEndpoint) return null;

  let start: KeypointAnnotation | undefined;
  let end: KeypointAnnotation | undefined;
  for (const keypoint of annotation.keypoints ?? []) {
    const parsed = parseMeasurementEndpointName(keypoint.name);
    if (!parsed || parsed.key !== parsedEndpoint.key) continue;
    if (parsed.endpoint === "start" && !start) start = keypoint;
    if (parsed.endpoint === "end" && !end) end = keypoint;
  }

  if (!start || !end) return null;
  return { key: parsedEndpoint.key, start, end };
}

/** Record the pre-edit value for an axis exactly once, and only if it had one. */
function appendMeasurementOverrideOnce(
  prior: UserAdjustment["measurementOverrides"],
  measurementField: string,
  originalValue: number | undefined
): NonNullable<UserAdjustment["measurementOverrides"]> {
  const measurementOverrides = [...(prior ?? [])];
  if (typeof originalValue !== "number" || !Number.isFinite(originalValue)) return measurementOverrides;
  if (measurementOverrides.some((override) => override.measurementField === measurementField)) {
    return measurementOverrides;
  }
  measurementOverrides.push({ measurementField, originalValue });
  return measurementOverrides;
}

/** Record the pre-edit position for an endpoint exactly once. */
function appendKeypointAdjustmentOnce(
  prior: UserAdjustment["keypointAdjustments"],
  keypointName: string,
  originalPosition: { x: number; y: number }
): NonNullable<UserAdjustment["keypointAdjustments"]> {
  const keypointAdjustments = [...(prior ?? [])];
  if (!keypointAdjustments.some((adjustment) => adjustment.name === keypointName)) {
    keypointAdjustments.push({ name: keypointName, originalPosition });
  }
  return keypointAdjustments;
}

/**
 * Apply one complete, depth-recomputed axis edit immutably.
 *
 * The endpoint must belong to a complete stored axis; the axis need not already
 * have a value, so a hand-drawn box (or an axis a 2D edit cleared) can record
 * its first measurement. A missing/invalid depth value rejects the whole edit so
 * the caller can snap the drag back without persisting geometry that disagrees
 * with its scalar measurement.
 */
export function applyMeasurementKeypointEdit(
  annotation: Annotation,
  keypointName: MeasurementKeypointName,
  newX: number,
  newY: number,
  recomputedMeters: number | null
): Annotation {
  if (recomputedMeters == null || !Number.isFinite(recomputedMeters) || recomputedMeters <= 0) return annotation;

  const axis = findMeasurementAxisForEndpoint(annotation, keypointName);
  const existing = annotation.keypoints?.find((keypoint) => keypoint.name === keypointName);
  if (!axis || !existing) return annotation;

  const originalMeasurement = getMeasurementAxisValue(annotation.measurements, axis.key);

  const keypoints: KeypointAnnotation[] = annotation.keypoints!.map((keypoint) =>
    keypoint.name === keypointName ? { ...keypoint, x: newX, y: newY } : keypoint
  );
  const measurements = withMeasurementAxisValue({ ...(annotation.measurements ?? {}) }, axis.key, recomputedMeters);
  const measurementField = measurementFieldPathForAxisKey(axis.key);

  const prior = annotation.userAdjustment;
  const keypointAdjustments = appendKeypointAdjustmentOnce(prior?.keypointAdjustments, keypointName, {
    x: existing.x,
    y: existing.y,
  });

  // Only a value that actually existed can be overridden; a first measurement
  // has no original to preserve.
  const measurementOverrides = appendMeasurementOverrideOnce(
    prior?.measurementOverrides,
    measurementField,
    originalMeasurement
  );

  const userAdjustment: UserAdjustment = {
    ...prior,
    manuallyCreated: prior?.manuallyCreated ?? false,
    keypointAdjustments,
    measurementOverrides,
  };

  return {
    ...annotation,
    keypoints,
    measurements,
    userAdjustment,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Apply one endpoint move immutably for a capture with no depth.
 *
 * There is nothing to recompute the axis value from, so the stored value is
 * REMOVED rather than left describing a shape the user just changed. The audit
 * trail still records the model's original position (and its original value, if
 * there was one) exactly once, so a later repair can tell what was overwritten.
 */
export function applyGeometryOnlyKeypointEdit(
  annotation: Annotation,
  keypointName: MeasurementKeypointName,
  newX: number,
  newY: number
): Annotation {
  const axis = findMeasurementAxisForEndpoint(annotation, keypointName);
  const existing = annotation.keypoints?.find((keypoint) => keypoint.name === keypointName);
  if (!axis || !existing) return annotation;
  // A drag that lands where it started is not an edit: it must not clear the
  // value or burn the one-shot provenance slot.
  if (existing.x === newX && existing.y === newY) return annotation;

  const keypoints: KeypointAnnotation[] = annotation.keypoints!.map((keypoint) =>
    keypoint.name === keypointName ? { ...keypoint, x: newX, y: newY } : keypoint
  );
  const originalMeasurement = getMeasurementAxisValue(annotation.measurements, axis.key);
  const measurements = withoutMeasurementAxisValue(annotation.measurements, axis.key);

  const prior = annotation.userAdjustment;
  const userAdjustment: UserAdjustment = {
    ...prior,
    manuallyCreated: prior?.manuallyCreated ?? false,
    keypointAdjustments: appendKeypointAdjustmentOnce(prior?.keypointAdjustments, keypointName, {
      x: existing.x,
      y: existing.y,
    }),
    measurementOverrides: appendMeasurementOverrideOnce(
      prior?.measurementOverrides,
      measurementFieldPathForAxisKey(axis.key),
      originalMeasurement
    ),
  };

  const next: Annotation = {
    ...annotation,
    keypoints,
    userAdjustment,
    updatedAt: new Date().toISOString(),
  };
  if (measurements) next.measurements = measurements;
  else delete next.measurements;
  return next;
}

/**
 * Value every complete axis of a freshly drawn box from depth at its seeded
 * endpoints, immutably.
 *
 * Without this a drawn box shows its seeded axes as lines with no number until
 * someone drags them, and the capture's averages silently leave it out. The
 * endpoints do not move, so this is a first value — not a user adjustment — and
 * no provenance is recorded. An axis depth cannot value stays empty.
 */
export function measureNewBoxAxes(
  annotation: Annotation,
  measureCm: (start: { x: number; y: number }, end: { x: number; y: number }) => number | null
): Annotation {
  let measurements = annotation.measurements;
  for (const spec of buildMeasurementLineDrawSpecs(annotation.keypoints)) {
    // Never overwrite: a box measured late (depth arrived after the draw) may
    // already carry a value the user produced.
    if (isMeasurementAxisValued(measurements, spec.key)) continue;
    const valueCm = measureCm(spec.start, spec.end);
    if (valueCm == null || !Number.isFinite(valueCm) || valueCm <= 0) continue;
    measurements = withMeasurementAxisValue({ ...(measurements ?? {}) }, spec.key, valueCm / 100);
  }
  return measurements === annotation.measurements ? annotation : { ...annotation, measurements };
}

export function isUserAdjustedMeasurementKeypoint(
  annotation: Annotation,
  keypointName: MeasurementKeypointName
): boolean {
  return (annotation.userAdjustment?.keypointAdjustments ?? []).some(
    (adjustment) => adjustment.name === keypointName && isMeasurementKeypointName(adjustment.name)
  );
}

// Lives in the measurement lib so lib-layer depth classification never imports
// component code; re-exported here for its historical callers.
export { deriveDepthCloudPath } from "@/lib/measurement/depthAvailability";

/** What a committed box move/resize does to the annotation's landmarks. */
export type BoxKeypointUpdate = {
  /** The full replacement landmark array, already transformed by the caller. */
  keypoints: KeypointAnnotation[];
  /**
   * Per axis key: a number to write, `null` to clear because the geometry moved
   * but nothing could measure it, absent to leave the stored value alone (a
   * rigid translate, where the pixel geometry — and its value — still hold).
   */
  axisValuesM: Record<string, number | null>;
};

/**
 * Apply a committed box change to an annotation immutably.
 *
 * Landmarks belong to their box: if the box moves and they do not, they stop
 * describing the object underneath, which is exactly the corrupt ground truth a
 * relabel task exists to avoid. Provenance mirrors the single-endpoint editors —
 * the pre-move position and the pre-move value are each recorded once, so a
 * second move never clobbers the true original.
 */
export function applyBoxChangeToKeypoints(
  annotation: Annotation,
  nextBBox: NonNullable<Annotation["bbox"]>,
  update: BoxKeypointUpdate
): Annotation {
  const priorByName = new Map(
    (annotation.keypoints ?? []).flatMap((keypoint) => (keypoint.name ? [[keypoint.name, keypoint] as const] : []))
  );

  let keypointAdjustments = annotation.userAdjustment?.keypointAdjustments;
  for (const keypoint of update.keypoints) {
    const before = keypoint.name ? priorByName.get(keypoint.name) : undefined;
    if (!before || (before.x === keypoint.x && before.y === keypoint.y)) continue;
    keypointAdjustments = appendKeypointAdjustmentOnce(keypointAdjustments, keypoint.name!, {
      x: before.x,
      y: before.y,
    });
  }

  let measurements = annotation.measurements ? { ...annotation.measurements } : undefined;
  let measurementOverrides = annotation.userAdjustment?.measurementOverrides;
  for (const [axisKey, valueM] of Object.entries(update.axisValuesM)) {
    const originalValue = getMeasurementAxisValue(measurements, axisKey);
    measurementOverrides = appendMeasurementOverrideOnce(
      measurementOverrides,
      measurementFieldPathForAxisKey(axisKey),
      originalValue
    );
    measurements =
      valueM === null
        ? withoutMeasurementAxisValue(measurements, axisKey)
        : withMeasurementAxisValue(measurements ?? {}, axisKey, valueM);
  }

  const priorAdjustment = annotation.userAdjustment;
  const userAdjustment: UserAdjustment = {
    ...priorAdjustment,
    manuallyCreated: priorAdjustment?.manuallyCreated ?? false,
    ...(keypointAdjustments?.length ? { keypointAdjustments } : {}),
    ...(measurementOverrides?.length ? { measurementOverrides } : {}),
  };

  return {
    ...annotation,
    bbox: nextBBox,
    ...(update.keypoints.length > 0
      ? {
          keypoints: update.keypoints,
          numKeypoints: update.keypoints.filter(({ visibility }) => visibility > 0).length,
        }
      : {}),
    ...(measurements ? { measurements } : {}),
    ...(measurements === undefined ? { measurements: undefined } : {}),
    userAdjustment,
    updatedAt: new Date().toISOString(),
  };
}
